import React, { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { GoogleLogo, FacebookLogo, TrendUp } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { formatCurrency, formatRoas, formatPercent, formatNumber } from "@/lib/format";
import api from "@/lib/api";

const PLATFORM_META = {
  google: { label: "Google Ads", icon: GoogleLogo, color: "#0EA5E9" },
  meta: { label: "Meta Ads", icon: FacebookLogo, color: "#06B6D4" },
};

export default function PlatformComparison() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCompare = useCallback(async () => {
    const res = await api.get("/campaigns/compare");
    setData(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCompare();
    const interval = setInterval(fetchCompare, 8000);
    return () => clearInterval(interval);
  }, [fetchCompare]);

  if (loading || !data) {
    return (
      <div className="h-40 flex items-center justify-center text-sm text-muted-foreground" data-testid="platform-comparison-loading">
        Loading comparison...
      </div>
    );
  }

  const chartData = [
    { metric: "ROAS", Google: data.google.avg_roas, Meta: data.meta.avg_roas },
    { metric: "CTR (%)", Google: data.google.avg_ctr, Meta: data.meta.avg_ctr },
    { metric: "CPC ($)", Google: data.google.avg_cpc, Meta: data.meta.avg_cpc },
  ];

  return (
    <div className="space-y-6" data-testid="platform-comparison-view">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["google", "meta"].map((platform) => {
          const meta = PLATFORM_META[platform];
          const stats = data[platform];
          const Icon = meta.icon;
          return (
            <Card key={platform} className="p-5" data-testid={`platform-compare-card-${platform}`}>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-md bg-secondary flex items-center justify-center">
                  <Icon className="h-4.5 w-4.5" style={{ color: meta.color }} />
                </div>
                <div>
                  <p className="font-heading font-semibold">{meta.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {stats.campaign_count} campaign{stats.campaign_count === 1 ? "" : "s"} · {stats.live_campaigns} live
                  </p>
                </div>
              </div>

              {stats.campaign_count === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid={`platform-compare-empty-${platform}`}>
                  No {meta.label} campaigns connected yet.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <Metric label="Spend" value={formatCurrency(stats.total_spend)} testId={`compare-${platform}-spend`} />
                  <Metric label="Conversions" value={formatNumber(stats.total_conversions)} testId={`compare-${platform}-conversions`} />
                  <Metric
                    label="Avg ROAS"
                    value={formatRoas(stats.avg_roas)}
                    highlight={stats.avg_roas >= 2}
                    testId={`compare-${platform}-roas`}
                  />
                  <Metric label="Avg CTR" value={formatPercent(stats.avg_ctr)} testId={`compare-${platform}-ctr`} />
                  <Metric label="Avg CPC" value={formatCurrency(stats.avg_cpc)} testId={`compare-${platform}-cpc`} />
                  <Metric label="Avg CPL" value={formatCurrency(stats.avg_cpl)} testId={`compare-${platform}-cpl`} />
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {(data.google.campaign_count > 0 || data.meta.campaign_count > 0) && (
        <Card className="p-5" data-testid="platform-compare-chart">
          <div className="flex items-center gap-2 mb-4">
            <TrendUp className="h-4 w-4 text-primary" weight="bold" />
            <p className="font-heading font-medium text-sm">Head-to-Head: ROAS · CTR · CPC</p>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" opacity={0.3} />
              <XAxis dataKey="metric" tick={{ fontSize: 12 }} stroke="currentColor" className="text-muted-foreground" />
              <YAxis tick={{ fontSize: 12 }} stroke="currentColor" className="text-muted-foreground" />
              <Tooltip
                contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Google" fill={PLATFORM_META.google.color} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Meta" fill={PLATFORM_META.meta.color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

function Metric({ label, value, highlight, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-base font-semibold tabular-nums ${highlight ? "text-success" : ""}`}>{value}</p>
    </div>
  );
}
